const logFactorialCache = [0];

export const logFactorial = (value) => {
  const n = Math.max(0, Math.floor(value));
  if (n <= 1) return 0;
  if (n < logFactorialCache.length) return logFactorialCache[n];
  const maxCache = 2000;
  let lastIndex = logFactorialCache.length - 1;
  let logValue = logFactorialCache[lastIndex];
  const upper = Math.min(n, maxCache);
  for (let i = lastIndex + 1; i <= upper; i += 1) {
    logValue += Math.log(i);
    logFactorialCache[i] = logValue;
  }
  if (n <= maxCache) return logFactorialCache[n];
  const x = n + 1;
  return (x - 0.5) * Math.log(x) - x + 0.5 * Math.log(2 * Math.PI);
};

export const computePoissonLogL = (observedCounts, expectedCounts) => {
  if (!observedCounts || !expectedCounts || observedCounts.length !== expectedCounts.length) return null;
  let logL = 0;
  for (let i = 0; i < observedCounts.length; i += 1) {
    const obs = Math.max(0, Math.round(observedCounts[i].count));
    const lambda = Math.max(1e-8, expectedCounts[i].count);
    logL += obs * Math.log(lambda) - lambda - logFactorial(obs);
  }
  return logL;
};

export const computeAIC = (logL, paramCount) => (
  Number.isFinite(logL) ? 2 * paramCount - 2 * logL : null
);

export const computeAICc = (logL, paramCount, sampleSize) => {
  if (!Number.isFinite(logL)) return null;
  const k = paramCount;
  const n = sampleSize;
  const aic = computeAIC(logL, k);
  if (!Number.isFinite(aic)) return null;
  if (!Number.isFinite(n) || n <= k + 1) return aic;
  return aic + (2 * k * (k + 1)) / (n - k - 1);
};

export const computeAkaikeWeights = (aicValues) => {
  if (!Array.isArray(aicValues) || aicValues.length === 0) return null;
  const finiteValues = aicValues.filter(Number.isFinite);
  if (finiteValues.length < 2) return null;
  const minAic = Math.min(...finiteValues);
  const rawWeights = aicValues.map((value) => (
    Number.isFinite(value) ? Math.exp(-0.5 * (value - minAic)) : 0
  ));
  const sum = rawWeights.reduce((acc, value) => acc + value, 0);
  if (!(sum > 0)) return null;
  return rawWeights.map(value => value / sum);
};

export const computeHoldoutRMSE = (observedCounts, expectedCounts, holdoutYears = 10) => {
  if (!observedCounts || !expectedCounts || observedCounts.length !== expectedCounts.length) return null;
  const n = Math.min(holdoutYears, observedCounts.length);
  if (n <= 0) return null;
  let sse = 0;
  for (let i = observedCounts.length - n; i < observedCounts.length; i += 1) {
    const diff = observedCounts[i].count - expectedCounts[i].count;
    sse += diff * diff;
  }
  return Math.sqrt(sse / n);
};

export const computeChiSquareStatistic = (observedCounts, expectedCounts) => {
  if (!observedCounts || !expectedCounts || observedCounts.length !== expectedCounts.length) return null;
  let stat = 0;
  for (let i = 0; i < observedCounts.length; i += 1) {
    const obs = observedCounts[i]?.count ?? 0;
    const exp = expectedCounts[i]?.count ?? 0;
    if (!Number.isFinite(obs) || !Number.isFinite(exp)) return null;
    const denom = exp > 0 ? exp : 1e-6;
    stat += ((obs - exp) ** 2) / denom;
  }
  return stat;
};
